import React, {useEffect} from 'react';
import {useLocation} from 'react-router-dom';

import {text} from '../text';
import {hooks} from '../hooks';
import {utils} from '../utils';
import {custom} from '../custom';
import {svg} from '../assets/svg';
import {theme} from '../constants';
import {components} from '../components';
import {authService} from '../services/authService';
import {setTokens} from '../store/slices/authSlice';
import { useLanguage } from '../context/LanguageContext';

export const SignIn: React.FC = () => {
  const {pathname} = useLocation();
  const location = useLocation();
  const navigate = hooks.useAppNavigate();
  const dispatch = hooks.useAppDispatch();
  const authSlice = hooks.useAppSelector(state => state.authSlice);
  const { t } = useLanguage();

  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [rememberMe, setRememberMe] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState('');

  // Auto redirect jika sudah authenticated
  useEffect(() => {
    if (authSlice.isAuthenticated && authSlice.accessToken) {
      navigate('/Task');
    }
  }, [authSlice.isAuthenticated, authSlice.accessToken, navigate]);

  useEffect(() => {
    const timer = setTimeout(() => {
      window.scrollTo(0, 0);
      const emailInput = document.querySelector('input[type="email"]') as HTMLInputElement;
      if (emailInput) {
        emailInput.focus();
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [pathname]);

  useEffect(() => {
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (location.pathname.includes('Onboarding')) {
      metaThemeColor?.setAttribute('content', '#040325');
    } else {
      metaThemeColor?.setAttribute('content', '#ffffff');
    }
  }, [location]);

  const handleLogin = async () => {
    try {
      setIsLoading(true);
      setErrorMessage('');

      if (!email || !password) {
        setErrorMessage(t.signIn.validation.fillAll);
        return;
      }

      if (!utils.validateEmail(email)) {
        setErrorMessage(t.signIn.validation.validEmail);
        return;
      }

      if (!utils.validatePassword(password)) {
        setErrorMessage(t.signIn.validation.passwordLength);
        return;
      }

      const response = await authService.login({
        email,
        password,
      });

      if (response.requireTenantSelect && response.tempToken && response.tenants) {
        // User punya lebih dari 1 tenant — arahkan ke tenant picker
        navigate('/TenantSelect', {
          state: { tempToken: response.tempToken, tenants: response.tenants },
        });
        return;
      }

      if (response.accessToken && response.refreshToken) {
        dispatch(
          setTokens({
            accessToken: response.accessToken,
            refreshToken: response.refreshToken,
            userId: response.user?.id,
            userName: response.user?.name,
            email: response.user?.email,
            groupId: (response.user as any)?.group_id,
            permissions: (response.user as any)?.permissions || [],
          })
        );

        setEmail('');
        setPassword('');
        setErrorMessage('');
        navigate('/Task');
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || JSON.stringify(error);
      setErrorMessage(errorMsg);
      console.error('Login error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderBackground = (): JSX.Element => {
    return <components.Background />;
  };

  const renderHeader = (): JSX.Element => {
    return <components.Header />;
  };

  const renderContent = (): JSX.Element => {
    return (
      <main
        className='container'
        style={{marginTop: 52, paddingTop: '18%', paddingBottom: 20, zIndex: 1}}
      >
        <div style={{display: 'flex', justifyContent: 'center', marginBottom: 20}}>
          <img src="images/logo.png" alt="logo" style={{width: 120, objectFit: 'contain'}} />
        </div>
        <text.H1 style={{marginBottom: 30, display: 'flex', justifyContent: 'center'}}>{t.signIn.welcome}</text.H1>
        {errorMessage && (
          <div
            style={{
              backgroundColor: '#FFE0E0',
              color: '#C00000',
              padding: '10px 15px',
              borderRadius: 8,
              marginBottom: 15,
              fontSize: 14,
            }}
          >
            {errorMessage}
          </div>
        )}
        <custom.InputField
          type='email'
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          leftIcon={<svg.EmailSvg />}
          rightIcon={<svg.CheckSvg />}
          containerStyle={{marginBottom: 10}}
          placeholder={t.signIn.emailPlaceholder}
          validateEmail={true}
          showValidationIcon={true}
        />
        <custom.InputField
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={t.signIn.passwordPlaceholder}
          clickable={true}
          leftIcon={<svg.PasswordSvg />}
          rightIcon={<svg.EyeOffSvg />}
          containerStyle={{marginBottom: 14}}
          isPasswordField={true}
        />
        <div style={{display: 'flex', justifyContent: 'flex-end', marginBottom: 14}}>
          <text.T16
            onClick={() => navigate('/ForgotPassword')}
            style={{cursor: 'pointer', color: theme.colors.mainColor}}
          >
            {(t as any).forgotPassword?.linkText || 'Forgot password?'}
          </text.T16>
        </div>
        <components.Button
          title={isLoading ? t.signIn.signingIn : t.signIn.button}
          containerStyle={{marginBottom: 14}}
          onClick={handleLogin}
        />
        {/* <div style={{...utils.rowCenter(), marginBottom: 40}}>
          <text.T16 style={{marginRight: 4}}>No account?</text.T16>
          <text.T16
            onClick={() => navigate('/SignUp')}
            style={{cursor: 'pointer', color: theme.colors.mainColor}}
          >
            Register now
          </text.T16>
        </div> */}
      </main>
    );
  };

  return (
    <div id='screen'>
      {renderBackground()}
      {renderHeader()}
      {renderContent()}
    </div>
  );
};
